import React, { useMemo, useState } from 'react';
import { MapPin, ChevronDown, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDeliveryLocation } from '@/contexts/DeliveryLocationContext';
import { useCitiesWithProducts } from '@/hooks/useProducts';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

interface DeliveryLocationSelectorProps {
  language: string;
}

export const DeliveryLocationSelector: React.FC<DeliveryLocationSelectorProps> = ({ language }) => {
  const { 
    selectedCity, 
    setSelectedCity, 
    availableCities 
  } = useDeliveryLocation();

  const { data: citiesWithProducts = [] } = useCitiesWithProducts();
  const navigate = useNavigate();

  // Fetch cities from database to get Arabic translations (Egypt only)
  const { data: dbCities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cities')
        .select(`
          name_en, 
          name_ar, 
          slug, 
          url_path,
          countries!inner (country_code)
        `)
        .eq('is_active', true)
        .eq('countries.country_code', 'eg')
        .order('name_en');
      
      if (error) throw error;
      return data;
    }
  });

  const isArabic = language === 'ar';

  const handleCitySelect = async (city: any) => {
    setSelectedCity(city);
    // Build the correct language-prefixed path
    const langPrefix = isArabic ? 'ar-eg' : 'en-eg';
    const cityPath = `/${langPrefix}/${city.slug}`;
    navigate(cityPath);
  };

  const handleClearSelection = () => {
    setSelectedCity(null);
    navigate('/');
  };

  // Use database cities if available, otherwise fallback to static data.
  const baseCities = dbCities.length > 0 ? dbCities : availableCities;

  // Quick search filter — 28 cities is too many to eyeball-scroll. Matches
  // either the English or Arabic name (case-insensitive). Hidden when there
  // are fewer than ~10 cities so it doesn't get in the way for small lists.
  const [search, setSearch] = useState('');
  const filteredCities = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseCities;
    return baseCities.filter((c: any) => {
      const en = (c.name_en || c.name || '').toLowerCase();
      const ar = (c.name_ar || '').toLowerCase();
      return en.includes(q) || ar.includes(q);
    });
  }, [baseCities, search]);
  const showSearch = baseCities.length > 10;

  // Helper function to get city name in correct language
  const getCityName = (city: any) => {
    if (isArabic && city.name_ar) {
      return city.name_ar;
    }
    return city.name_en || city.name;
  };

  const getDisplayText = () => {
    if (selectedCity) {
      return getCityName(selectedCity);
    }
    return isArabic ? 'اختر الموقع' : 'Select Location';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 px-2 py-1 rounded-xl shadow-sm border border-blue-100 hover:bg-blue-50 transition-all min-w-0 sm:gap-3 sm:px-4 sm:py-2"
          style={{ minWidth: 0 }}
        >
          <span className="flex items-center gap-1 sm:gap-2 px-1 py-0.5 sm:px-2 sm:py-1">
            <img src="/egypt-flag.svg" alt="Egypt Flag" className="h-4 w-4 sm:h-5 sm:w-5 rounded-sm" />
            <span className="font-semibold text-xs sm:text-sm">Egypt</span>
          </span>
          <span className="font-medium text-sm sm:text-base text-blue-900 truncate max-w-[90px] sm:max-w-none">
            {selectedCity
              ? (isArabic
                  ? `توصيل إلى ${getCityName(selectedCity)}`
                  : `Deliver To ${getCityName(selectedCity)}`)
              : (isArabic ? 'اختر الموقع' : 'Deliver To ?')}
          </span>
          <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-blue-700 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-64 max-h-[min(70vh,560px)] overflow-hidden flex flex-col p-0"
        align="start"
      >
        {/* Fixed header — label, optional search box, current-selection clear. */}
        <div className="flex-shrink-0 px-2 pt-2">
          <DropdownMenuLabel className="px-2">
            {isArabic ? 'اختر موقع التوصيل' : 'Select Delivery Location'}
          </DropdownMenuLabel>

          {showSearch && (
            <div className="relative mb-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={isArabic ? 'ابحث عن مدينة…' : 'Search cities…'}
                className="h-8 pl-7 pr-7 text-sm"
                // Stop the dropdown from intercepting keyboard navigation
                // (arrow keys, space) when the user is typing in the search.
                onKeyDown={(e) => e.stopPropagation()}
                dir={isArabic ? 'rtl' : 'ltr'}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                  aria-label="Clear search"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
          )}
        </div>

        <DropdownMenuSeparator className="flex-shrink-0 my-0" />

        {selectedCity && (
          <div className="flex-shrink-0">
            <DropdownMenuItem onClick={handleClearSelection}>
              {isArabic ? 'إلغاء التحديد' : 'Clear Selection'}
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-0" />
          </div>
        )}

        {/* Scrollable list — capped so the dropdown never grows past the viewport.
            scrollbarWidth=thin keeps it from looking like a desktop scrollbar. */}
        <div
          className="flex-1 overflow-y-auto py-1"
          style={{ scrollbarWidth: 'thin' }}
        >
          {filteredCities.length === 0 ? (
            <div className="px-3 py-6 text-sm text-center text-muted-foreground">
              {isArabic ? 'مفيش مدن مطابقة' : 'No cities match'}
            </div>
          ) : (
            filteredCities.map((city) => (
              <DropdownMenuItem
                key={city.slug}
                onClick={() => handleCitySelect(city)}
              >
                <span>{getCityName(city)}</span>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};